import { FormControlLabel, IconButton, InputAdornment, MenuItem, Select, Switch, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import MinusIcon from "@mui/icons-material/Remove";
import PlusIcon from "@mui/icons-material/Add";
import { TaskDataRowDataType } from "ui/Scripts/source/models/enums";
import { ITaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { DatePicker, TimePicker } from "source/components/framework/DateTime";
import { Translations } from "ui/Scripts/source/models/translations";
import { FormInteger } from "source/components/framework/FormInteger";
import { FormDecimal } from "source/components/framework/FormDecimal";
import { FormTimeSpan } from "source/components/framework/FormTimeSpan";
import { Base } from "ui/Scripts/source/framework/base";
import { ITaskTypeFieldPropUpdate } from "../../FormDesignerTypes";

interface IFormDesignerComponentPropertiesDefaultValueProps {
    disabled?: boolean;
    required?: boolean;
    taskTypeCol?: ITaskTypeCol;
    onTaskTypePropUpdate?: (values: ITaskTypeFieldPropUpdate[]) => void;
}

export const FormDesignerComponentPropertiesDefaultValue = (
    props: IFormDesignerComponentPropertiesDefaultValueProps
) => {
    const [valueStr, setValueStr] = useState("");
    const [relative, setRelative] = useState<{ minus: boolean, offset: number }>(null);

    useEffect(() => {
        let val = props.taskTypeCol?.defaultStr ?? "";
        if (props.taskTypeCol?.dataType === TaskDataRowDataType.Boolean && !val) {
            val = "0";
        }
        setValueStr(val);
        if ((props.taskTypeCol?.dataType === TaskDataRowDataType.Date || props.taskTypeCol?.dataType === TaskDataRowDataType.Time) && val.length > 1) {
            const hasMinus = val[0] === "-";
            const hasPlus = val[0] === "+";
            setRelative(hasMinus || hasPlus ? { minus: hasMinus, offset: val.substring(1).toInteger() } : null);
        } else {
            setRelative(null);
        }
    }, [props.taskTypeCol?.dataType, props.taskTypeCol?.defaultStr]);
        
    const handleValueFieldChange = (event) => {
        props.onTaskTypePropUpdate([{
            field: props.taskTypeCol?.field,
            name: "defaultStr",
            value: event.target.value
        }]);
    };

    const handleValueStrChange = (value: string) => {
        props.onTaskTypePropUpdate([{
            field: props.taskTypeCol?.field,
            name: "defaultStr",
            value
        }]);
    };

    const handleValueNumberChange = (value: number) => {
        props.onTaskTypePropUpdate([{
            field: props.taskTypeCol?.field,
            name: "defaultNbr",
            value
        }]);
    };

    const handleValueRelativeTimeChange = (offset: number, minus: boolean) => {
        props.onTaskTypePropUpdate([{
            field: props.taskTypeCol?.field,
            name: "defaultStr",
            value: (minus ? "-" : "+") + offset.toString(10)
        }]);
    };

    const isDropdown = props.taskTypeCol?.options?.length > 0 || props.taskTypeCol?.idOptions?.length > 0;

    return (
        <>
            {props.taskTypeCol?.dataType === TaskDataRowDataType.String && isDropdown && (
                <Select value={valueStr} onChange={handleValueFieldChange}>
                    {!props.required &&
                        <MenuItem key={""} value="">&nbsp;</MenuItem>
                    }
                    {props.taskTypeCol?.options?.length > 0 && props.taskTypeCol?.options?.map((item) => (
                        <MenuItem key={item.replace(" ", "")} value={item}>{item}</MenuItem>
                    ))}
                    {props.taskTypeCol?.idOptions?.length > 0 && props.taskTypeCol?.idOptions?.map((item) => (
                        <MenuItem key={item.id} value={item.id}>{item.title}</MenuItem>
                    ))}
                </Select>
            )}
            {(props.taskTypeCol?.dataType === TaskDataRowDataType.String && !isDropdown) &&
                <TextField
                    value={valueStr}
                    onChange={handleValueFieldChange}
                />
            }
            {props.taskTypeCol?.dataType === TaskDataRowDataType.Boolean && (
                <Select value={valueStr} onChange={handleValueFieldChange} disabled={props.disabled}>
                    <MenuItem key={"0"} value={"0"}>{Translations.FormFalse}</MenuItem>
                    <MenuItem key={"1"} value={"1"}>{Translations.FormTrue}</MenuItem>
                </Select>
            )}
            {props.taskTypeCol?.dataType === TaskDataRowDataType.Integer &&
                <FormInteger
                    max={props.taskTypeCol?.maximum}
                    min={props.taskTypeCol?.minimum ?? -99999999}
                    maxLength={9}
                    value={props.taskTypeCol?.defaultNbr}
                    required={false}
                    onChange={handleValueNumberChange}
                />
            }
            {(props.taskTypeCol?.dataType === TaskDataRowDataType.Number) &&
                <FormDecimal
                    max={props.taskTypeCol?.maximum}
                    min={props.taskTypeCol?.minimum ?? -9999999999}
                    decimals={props.taskTypeCol?.decimals ?? 0}
                    maxLength={11}
                    value={props.taskTypeCol?.defaultNbr}
                    required={false}
                    onChange={handleValueNumberChange}
                />
            }
            {props.taskTypeCol?.dataType === TaskDataRowDataType.Date &&
                <>
                    <div style={{ marginBottom: "0.5rem" }}>
                        <FormControlLabel
                            sx={{ marginLeft: 0 }}
                            control={<Switch
                                checked={!!relative}
                                onChange={(e, checked) => {
                                    handleValueStrChange(checked ? "-1" : "");
                                }}
                            />}
                            label={Translations.FormRelativeDefinition}
                        />
                    </div>
                    {(relative
                        ? <>
                            <FormInteger
                                max={99999999}
                                min={0}
                                maxLength={9}
                                value={relative.offset}
                                required={true}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">
                                        <IconButton
                                            onClick={(e) => handleValueRelativeTimeChange(relative.offset, !relative.minus)}
                                            edge="start"
                                        >
                                            {relative.minus ? <MinusIcon /> : <PlusIcon />}
                                        </IconButton>
                                    </InputAdornment>,
                                    endAdornment: <InputAdornment position="end">{Translations.Days.toLocaleLowerCase()}</InputAdornment>
                                }}
                                onChange={(value) => handleValueRelativeTimeChange(value, relative.minus)}
                            />
                        </>
                        : <DatePicker
                            value={valueStr}
                            onChange={handleValueStrChange}
                        />
                    )}
                </>
            }
            {props.taskTypeCol?.dataType === TaskDataRowDataType.Time &&
                <>
                    <div style={{ marginBottom: "0.5rem" }}>
                        <FormControlLabel
                            sx={{ marginLeft: 0 }}
                            control={<Switch
                                checked={!!relative}
                                onChange={(e, checked) => {
                                    handleValueStrChange(checked ? "-60" : "");
                                }}
                            />}
                            label={Translations.FormRelativeDefinition}
                        />
                    </div>
                    {(relative
                        ? <>
                            <FormInteger
                                max={99999999}
                                min={0}
                                maxLength={9}
                                value={relative.offset}
                                required={true}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">
                                        <IconButton
                                            onClick={(e) => handleValueRelativeTimeChange(relative.offset, !relative.minus)}
                                            edge="start"
                                        >
                                            {relative.minus ? <MinusIcon /> : <PlusIcon />}
                                        </IconButton>
                                    </InputAdornment>,
                                    endAdornment: <InputAdornment position="end">{Translations.Minutes.toLocaleLowerCase()}</InputAdornment>
                                }}
                                onChange={(value) => handleValueRelativeTimeChange(value, relative.minus)}
                            />
                        </>
                        : <TimePicker
                            value={valueStr}
                            onChange={handleValueStrChange}
                        />
                    )}
                </>
            }
            {props.taskTypeCol?.dataType === TaskDataRowDataType.TimeSpan &&
                <FormTimeSpan
                    maxLength={10}
                    value={props.taskTypeCol?.defaultNbr}
                    required={false}
                    onChange={(value) => handleValueStrChange(Base.timeSpanToString(value))}
                />
            }
        </>
    );
};
